import { useRef, useState } from "react";
import { ImagePlus, Link2, X } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, resolveImage, uploadImage } from "@/lib/api";
import { Input } from "@/components/ui/input";

export const ImageUpload = ({ value, onChange, testId = "image-upload", height = "h-40", allowUrl = true }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlMode, setUrlMode] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      onChange(await uploadImage(file));
      toast.success("Image uploaded");
    } catch (e) { toast.error(errorMessage(e)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (value) {
    return (
      <div className="relative rounded-xl overflow-hidden border border-slate-200">
        <img src={resolveImage(value)} alt="" className={`w-full ${height} object-cover`} data-testid={`${testId}-preview`} />
        <button type="button" onClick={() => onChange(null)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 grid place-items-center text-slate-700 hover:text-rose-600" aria-label="Remove image" data-testid={`${testId}-remove`}><X className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {urlMode ? (
        <div className="flex gap-2">
          <Input placeholder="https://…" className="rounded-xl" data-testid={`${testId}-url-input`}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (e.currentTarget.value.trim()) onChange(e.currentTarget.value.trim()); } }}
            onBlur={(e) => { if (e.target.value.trim()) onChange(e.target.value.trim()); }} autoFocus />
          <button type="button" onClick={() => setUrlMode(false)} className="text-xs text-slate-500 hover:text-slate-900 px-2" data-testid={`${testId}-url-cancel`}>Cancel</button>
        </div>
      ) : (
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${testId}-button`}
          className={`w-full ${height} rounded-xl border-2 border-dashed border-slate-300 hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors flex flex-col items-center justify-center gap-2 text-sm text-slate-500`}>
          <ImagePlus className="w-6 h-6" /> {uploading ? "Uploading…" : "Upload image (JPG, PNG, WebP, GIF · max 8 MB)"}
        </button>
      )}
      {allowUrl && !urlMode && (
        <button type="button" onClick={() => setUrlMode(true)} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1" data-testid={`${testId}-use-url`}><Link2 className="w-3 h-3" /> Paste an image link instead</button>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testId}-file-input`} />
    </div>
  );
};
